import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';
import styles from './styles.module.scss';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';
import CompanyService from '../../../../../services/users/company';

// props
import { IPaginationResourceShort } from '../../../../../props/general';
import { ICompanyResourceShort } from '../../../../../props/users/company';

// components
import { ActionButton, CommandBar, IColumn, ICommandBarItemProps, IContextualMenuItem, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack, TooltipHost } from '@fluentui/react';
import Text from '../../../../typography/text';
import CompanyForm from '../form';
import GlobalConfig from '../../../../../config';
import Pagination from '../../../../uiframeworks/pagination';
import { NavLink } from 'react-router-dom';
import PermissionsService from '../../../../../services/permissions';
import CompanyStatus from '../../../../../manifests/status/company';
import Tag from '../../../../uiframeworks/tag';
import VerifyCompanyDocumentsForm from '../form/verifyDocuments';
import ResetCompanyVerificationForm from '../form/resetVerification';
import VerifyCompanyForm from '../form/verifyCompany';

interface ICompaniesListProps {
}

const CompaniesList: React.FC<ICompaniesListProps> = (props: ICompaniesListProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [showingMore, setShowingMore] = React.useState<boolean>(false);
    const [companies, setCompanies] = React.useState<ICompanyResourceShort[]>([]);
    const [activeCompany, setActiveCompany] = React.useState<ICompanyResourceShort | undefined>();
    const [pagination, setPagingation] = React.useState<IPaginationResourceShort | undefined>();
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const hasPermission = PermissionsService.hasPermission(['companies.read.all'], user.permissions);

    const columns: IColumn[] = [
        {
            key: 'name',
            name: 'Name',
            minWidth: 250,
            maxWidth: 250,
            onRender: (item: ICompanyResourceShort) => {
                return <Stack.Item styles={{ root: { padding: '4px 0px' } }}>
                    <TooltipHost content={item.name}>
                        <NavLink to={`/users/companies/${item.id}`}>
                            <Text size={"small"}>{item.name}</Text>
                        </NavLink>
                    </TooltipHost>
                </Stack.Item>;
            },
        },
        {
            key: "actions",
            name: "",
            minWidth: 50,
            maxWidth: 50,
            onRender: (item: ICompanyResourceShort) => {
                let menuItems: IContextualMenuItem[] = [];
                let approvalStage = 'documents_approval';
                if (item.verificationStatus === 'approved') {
                    approvalStage = 'completed';
                } else if (item.verificationStatus === 'documents_verified') {
                    approvalStage = 'company_approval';
                } else if (item.verificationStatus === 'rejected' || 'documents_rejected') {
                    approvalStage = 'documents_approval';
                }

                if (PermissionsService.hasPermission(['companies.update.all'], user.permissions)) {
                    menuItems.push({
                        key: "update", text: "Update details", iconProps: { iconName: "Edit" }, onClick: () => {
                            setActiveCompany(item);
                            setActiveSurface('update');
                        }
                    });
                }

                if ((user.data?.companies || []).map((c) => c.id).indexOf(item.id) > -1 && PermissionsService.hasPermission(['companies.update.me'], user.permissions)) {
                    menuItems.push({
                        key: "update", text: "Update details", iconProps: { iconName: "Edit" }, onClick: () => {
                            setActiveCompany(item);
                            setActiveSurface('update');
                        }
                    });
                }

                if (PermissionsService.hasPermission(['companies.approvals.documents_verify.all', 'companies.approvals.documents_reject.all'], user.permissions) && approvalStage === 'documents_approval') {
                    menuItems.push({
                        key: "verify_documents", text: "Approve/reject documents", iconProps: { iconName: "CheckMark" }, onClick: () => {
                            setActiveCompany(item);
                            setActiveSurface('company.documents.verify');
                        }
                    });
                }

                if (PermissionsService.hasPermission(['companies.approvals.approve.all', 'companies.approvals.reject.all'], user.permissions) && approvalStage === 'company_approval') {
                    menuItems.push({
                        key: "verify_company", text: "Approve/reject company", iconProps: { iconName: "CheckMark" }, onClick: () => {
                            setActiveCompany(item);
                            setActiveSurface('company.verify');
                        }
                    });
                }

                if (PermissionsService.hasPermission(['companies.approvals.reset.all'], user.permissions) && (approvalStage === 'completed' || approvalStage === 'company_approval' || item.verificationStatus === 'documents_rejected')) {
                    menuItems.push({
                        key: "reset", text: "Reset verification", iconProps: { iconName: "Refresh" }, onClick: () => {
                            setActiveCompany(item);
                            setActiveSurface('company.approvals.reset');
                        }
                    });
                }

                return <Stack.Item styles={{ root: { padding: '2px 0px' } }}>
                    {menuItems.length > 0 ? <Stack.Item className={"detailsListActionRow"}>
                        <ActionButton className={'detailsListActionButton'} menuProps={{
                            items: menuItems
                        }} />
                    </Stack.Item> : null}
                </Stack.Item>;
            }
        },
        {
            key: 'address',
            name: 'Address',
            minWidth: 0,
            isMultiline: true,
            onRender: (item: ICompanyResourceShort) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={"small"}>{item.address || "-"}</Text>
                </Stack>;
            },
        },
        {
            key: 'phoneNumber',
            name: 'Phone Number',
            minWidth: 200,
            maxWidth: 200,
            onRender: (item: ICompanyResourceShort) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={"small"}>{item.phoneNumber || "-"}</Text>
                </Stack>;
            },
        },
        {
            key: 'status',
            name: 'Status',
            minWidth: 200,
            maxWidth: 200,
            onRender: (item: ICompanyResourceShort) => {
                const status = CompanyStatus.find((s) => s.key === (item.verificationStatus || "").toLowerCase());
                return <Stack horizontal>
                    <Tag size={'small'} variant={status?.tagVariant} text={status?.text} />
                </Stack>
            },
        },
    ];

    useEffect(() => {
        _onRetrieveCompanies();
    }, [keyword]);

    const _onRetrieveCompanies = async (pageNumber?: number,) => {
        try {
            setLoaded(false);
            const qs: string[] = [];
            qs.push(`top=${GlobalConfig.defaultTop}`);
            if (pageNumber) { qs.push(`page=${pageNumber}`); }
            if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }

            const result = await CompanyService.retrieve(qs.join("&"));
            setCompanies(result.data);
            setPagingation(result.pagination);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_companies_error',
                text: 'Failed to retrieve companies list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        if (PermissionsService.hasPermission(['companies.read.all', 'companies.read.me'], user.permissions)) {
            items.push({
                key: "search",
                onRender: () => {
                    return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} />
                }
            });
        }

        if (PermissionsService.hasPermission(['companies.create'], user.permissions)) {
            farItems.push({
                key: "register",
                text: "Create Company",
                iconProps: { iconName: "Add" },
                onRender: () => {
                    return <PrimaryButton text={"Create Company"}
                        iconProps={{ iconName: "Add" }}
                        onClick={() => { setActiveSurface('create') }}
                        styles={{ root: { marginLeft: 10 } }} />;
                }
            });
        }

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActiveCompany(undefined);

        if (refresh) { _onRetrieveCompanies() }
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{
                root: {
                    padding: 0,
                    height: 'unset',
                    backgroundColor: 'transparent'
                }
            }}
            ariaLabel="Use left and right arrow keys to navigate between commands" />
        <Stack tokens={{ childrenGap: 10 }}>
            {pagination && companies.length > 0 ? <Pagination goToPage={_onRetrieveCompanies} currentPage={pagination.currentPage} totalPage={pagination.lastPage} text={pagination.total + " company(s) found"} /> : null}
            <Stack className={styles.list}>
                {
                    !loaded || (loaded && companies.length > 0) ? (
                        <>
                            <ShimmeredDetailsList
                                setKey="items"
                                items={companies}
                                columns={columns}
                                selectionMode={SelectionMode.none}
                                enableShimmer={!loaded}
                                onShouldVirtualize={() => false}
                                ariaLabelForShimmer="Content is being fetched"
                                ariaLabelForGrid="Item details" />
                        </>
                    ) : null
                }
                {loaded && companies.length < 1 ? <Stack styles={{ root: { padding: 10 } }}>
                    <Text>Company(s) not found</Text>
                </Stack> : null}
            </Stack>
            {pagination && companies.length > 0 ? <Pagination goToPage={_onRetrieveCompanies} currentPage={pagination.currentPage} totalPage={pagination.lastPage} /> : null}
        </Stack>
        {activeSurface === 'create' ? <CompanyForm onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'update' && activeCompany ? <CompanyForm companyId={activeCompany.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'company.documents.verify' && activeCompany ? <VerifyCompanyDocumentsForm companyId={activeCompany.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'company.approvals.reset' && activeCompany ? <ResetCompanyVerificationForm companyId={activeCompany.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'company.verify' && activeCompany ? <VerifyCompanyForm companyId={activeCompany.id} onDismissed={_onSurfaceDismissed} /> : null}
    </Stack>;
};

export default CompaniesList;
